import { pushToDataLayer } from './dataLayer';
import { AnalyticsEvents } from './events';
import { normalizeIndustry, normalizePlanTier } from './industryPlan';

const PURCHASE_DEDUP_KEY = 'nf_analytics_purchases_v1';
const MAX_DEDUP_ENTRIES = 100;

/**
 * @param {string} eventName
 * @param {Record<string, unknown>} [parameters]
 */
export function trackEvent(eventName, parameters = {}) {
  if (!eventName) return;
  const payload = { event: eventName, ...parameters };
  pushToDataLayer(payload);
}

/**
 * SPA page_view — gtag config uses send_page_view: false; this event handles route changes.
 * @param {string} path
 * @param {string} [title]
 */
export function trackPageView(path, title) {
  if (typeof window === 'undefined') return;
  const pagePath = path || window.location.pathname + window.location.search;
  trackEvent(AnalyticsEvents.PAGE_VIEW, {
    page_path: pagePath,
    page_location: window.location.href,
    page_title: title || document.title || '',
  });
}

export function trackViewFeatures(extra = {}) {
  trackEvent(AnalyticsEvents.VIEW_FEATURES, extra);
}

export function trackViewPricing(extra = {}) {
  trackEvent(AnalyticsEvents.VIEW_PRICING, extra);
}

export function trackViewDemo(extra = {}) {
  trackEvent(AnalyticsEvents.VIEW_DEMO, extra);
}

export function trackClickContact(extra = {}) {
  trackEvent(AnalyticsEvents.CLICK_CONTACT, extra);
}

export function trackClickWhatsapp(extra = {}) {
  trackEvent(AnalyticsEvents.CLICK_WHATSAPP, extra);
}

export function trackClickDemo(extra = {}) {
  trackEvent(AnalyticsEvents.CLICK_DEMO, extra);
}

export function trackSignupStarted(extra = {}) {
  trackEvent(AnalyticsEvents.SIGNUP_STARTED, extra);
}

export function trackSignupCompleted(extra = {}) {
  trackEvent(AnalyticsEvents.SIGNUP_COMPLETED, extra);
}

export function trackEmailVerified(extra = {}) {
  trackEvent(AnalyticsEvents.EMAIL_VERIFIED, extra);
}

export function trackLogin(extra = {}) {
  trackEvent(AnalyticsEvents.LOGIN, extra);
}

export function trackIndustrySelected(industry, extra = {}) {
  const normalized = normalizeIndustry(industry);
  trackEvent(AnalyticsEvents.INDUSTRY_SELECTED, {
    ...(normalized ? { industry: normalized } : {}),
    ...extra,
  });
}

export function trackPlanSelected(plan, industry, extra = {}) {
  const normalizedPlan = normalizePlanTier(plan);
  const normalizedIndustry = normalizeIndustry(industry);
  trackEvent(AnalyticsEvents.PLAN_SELECTED, {
    ...(normalizedPlan ? { plan: normalizedPlan } : {}),
    ...(normalizedIndustry ? { industry: normalizedIndustry } : {}),
    ...extra,
  });
  trackEvent(AnalyticsEvents.SELECT_PLAN, {
    ...(normalizedPlan ? { plan: normalizedPlan } : {}),
    ...(normalizedIndustry ? { industry: normalizedIndustry } : {}),
    ...extra,
  });
}

export function trackStartTrial(industry, plan, extra = {}) {
  trackEvent(AnalyticsEvents.START_TRIAL, {
    ...(normalizeIndustry(industry) ? { industry: normalizeIndustry(industry) } : {}),
    ...(normalizePlanTier(plan) ? { plan: normalizePlanTier(plan) } : {}),
    ...extra,
  });
}

export function trackBeginCheckout({ industry, plan, currency = 'INR', value } = {}) {
  trackEvent(AnalyticsEvents.BEGIN_CHECKOUT, {
    ...(normalizeIndustry(industry) ? { industry: normalizeIndustry(industry) } : {}),
    ...(normalizePlanTier(plan) ? { plan: normalizePlanTier(plan) } : {}),
    currency,
    ...(value != null && !Number.isNaN(Number(value)) ? { value: Number(value) } : {}),
  });
}

function hasTrackedPurchase(transactionId) {
  if (!transactionId || typeof sessionStorage === 'undefined') return false;
  try {
    const seen = JSON.parse(sessionStorage.getItem(PURCHASE_DEDUP_KEY) || '[]');
    return Array.isArray(seen) && seen.includes(String(transactionId));
  } catch {
    return false;
  }
}

function markPurchaseTracked(transactionId) {
  if (!transactionId || typeof sessionStorage === 'undefined') return;
  try {
    const prev = JSON.parse(sessionStorage.getItem(PURCHASE_DEDUP_KEY) || '[]');
    const list = Array.isArray(prev) ? prev : [];
    const next = [...list.filter((id) => id !== String(transactionId)), String(transactionId)].slice(-MAX_DEDUP_ENTRIES);
    sessionStorage.setItem(PURCHASE_DEDUP_KEY, JSON.stringify(next));
  } catch {
    /* ignore storage errors */
  }
}

/**
 * @param {{ transaction_id: string, value?: number, currency?: string, industry?: string, plan?: string }} params
 */
export function trackPurchase(params = {}) {
  const transactionId = params.transaction_id ? String(params.transaction_id) : '';
  if (transactionId && hasTrackedPurchase(transactionId)) return;

  trackEvent(AnalyticsEvents.PURCHASE, {
    ...(transactionId ? { transaction_id: transactionId } : {}),
    currency: params.currency || 'INR',
    ...(params.value != null && !Number.isNaN(Number(params.value)) ? { value: Number(params.value) } : {}),
    ...(normalizeIndustry(params.industry) ? { industry: normalizeIndustry(params.industry) } : {}),
    ...(normalizePlanTier(params.plan) ? { plan: normalizePlanTier(params.plan) } : {}),
  });

  if (transactionId) markPurchaseTracked(transactionId);
}

export function trackSubscriptionCancelled(extra = {}) {
  trackEvent(AnalyticsEvents.SUBSCRIPTION_CANCELLED, extra);
}

export function trackGenerateLead(extra = {}) {
  trackEvent(AnalyticsEvents.GENERATE_LEAD, extra);
}

export function trackDemoRequest(extra = {}) {
  trackEvent(AnalyticsEvents.DEMO_REQUEST, extra);
}

export function trackContactFormSubmit(extra = {}) {
  trackEvent(AnalyticsEvents.CONTACT_FORM_SUBMIT, extra);
}

export function trackCallbackRequest(extra = {}) {
  trackEvent(AnalyticsEvents.CALLBACK_REQUEST, extra);
}

export function trackAgencyCreated(industry, extra = {}) {
  trackEvent(AnalyticsEvents.AGENCY_CREATED, {
    ...(normalizeIndustry(industry) ? { industry: normalizeIndustry(industry) } : {}),
    ...extra,
  });
}
